import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CAGRCalculator = () => {
  const [initialValue, setInitialValue] = useState('');
  const [finalValue, setFinalValue] = useState('');
  const [years, setYears] = useState('');
  const [cagr, setCAGR] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);
  const [solutionSteps, setSolutionSteps] = useState([]);

  const handleInitialValueChange = (e) => {
    setInitialValue(e.target.value);
    setError('');
  };

  const handleFinalValueChange = (e) => {
    setFinalValue(e.target.value);
    setError('');
  };

  const handleYearsChange = (e) => {
    setYears(e.target.value);
    setError('');
  };

  const calculateCAGR = () => {
    if (!initialValue || !finalValue || !years) {
      setError('Please enter all required values.');
      return;
    }

    const initial = parseFloat(initialValue);
    const final = parseFloat(finalValue);
    const yearCount = parseFloat(years);

    if (isNaN(initial) || isNaN(final) || isNaN(yearCount) || initial <= 0 || yearCount <= 0) {
      setError('Please enter valid numbers and ensure initial value and number of years are positive.');
      return;
    }

    const cagrValue = ((final / initial) ** (1 / yearCount) - 1) * 100;
    setCAGR(cagrValue.toFixed(2));

    // Update chart data
    setChartData({
      labels: ['Initial Value', 'Final Value', 'CAGR (%)'],
      datasets: [
        {
          label: 'CAGR Calculation',
          data: [initial, final, cagrValue],
          backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(54, 162, 235, 0.5)', 'rgba(255, 206, 86, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(54, 162, 235, 1)', 'rgba(255, 206, 86, 1)'],
          borderWidth: 1
        }
      ]
    });

    // Update solution steps
    setSolutionSteps([
      `Initial Value: ${initial.toFixed(2)}`,
      `Final Value: ${final.toFixed(2)}`,
      `Number of Years: ${yearCount.toFixed(2)}`,
      `CAGR Calculation: ((Final Value / Initial Value) ** (1 / Number of Years) - 1) * 100`,
      `CAGR: ((${final.toFixed(2)} / ${initial.toFixed(2)}) ** (1 / ${yearCount.toFixed(2)}) - 1) * 100 = ${cagrValue.toFixed(2)}%`
    ]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    calculateCAGR();
  };

  return (
    <>
      <Helmet>
        <title>CAGR Calculator | BioLab Tools</title>
        <meta name="description" content="Calculate the Compound Annual Growth Rate (CAGR) of an investment with ease. Determine the annual growth rate over a specified period using this calculator." />
        <meta name="keywords" content="CAGR calculator, compound annual growth rate, investment calculator, financial calculator" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          <header className="mb-8 text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">🔍</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">CAGR Calculator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Calculate the Compound Annual Growth Rate (CAGR) of an investment with ease. Determine the annual growth rate over a specified period.
            </p>
          </header>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Initial Value</label>
                <input
                  type="number"
                  value={initialValue}
                  onChange={handleInitialValueChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter initial value"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Final Value</label>
                <input
                  type="number"
                  value={finalValue}
                  onChange={handleFinalValueChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter final value"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Number of Years</label>
                <input
                  type="number"
                  value={years}
                  onChange={handleYearsChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter number of years"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-green-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-green-700 transition duration-200"
              >
                Calculate CAGR
              </button>

              {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-md mt-4">
                  {error}
                </div>
              )}

              {cagr !== null && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Result</h2>
                  <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <p className="text-lg font-medium">{cagr}%</p>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Visualization</h3>
                    <div className="h-96">
                      <Bar
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: { display: true, text: 'Value' }
                            },
                            x: { title: { display: true, text: 'Category' } }
                          },
                          plugins: { title: { display: true, text: 'CAGR Calculation' } }
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Solution Steps</h3>
                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                      <ul className="list-disc pl-6 space-y-2">
                        {solutionSteps.map((step, index) => (
                          <li key={index} className="text-gray-600">{step}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Frequently Asked Questions (FAQ)</h2>
            <div className="space-y-6">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What is CAGR?</h3>
                <p className="text-gray-600">
                  CAGR stands for Compound Annual Growth Rate. It is a measure of the mean annual growth rate of an investment over a specified period of time longer than one year.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How is CAGR calculated?</h3>
                <p className="text-gray-600">
                  CAGR is calculated using the formula: CAGR = ((Final Value / Initial Value) ** (1 / Number of Years) - 1) * 100.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Can I use this calculator for financial analysis?</h3>
                <p className="text-gray-600">
                  Yes, this calculator is perfect for financial analysis, helping you understand the average annual growth rate of an investment over a specified period.
                </p>
              </div>
            </div>
          </div>

          {/* How to Use Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use the CAGR Calculator</h2>
            <div className="space-y-8">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Initial Value</h3>
                <p className="text-gray-600">
                  Input the initial value of the investment.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Final Value</h3>
                <p className="text-gray-600">
                  Input the final value of the investment.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Enter Number of Years</h3>
                <p className="text-gray-600">
                  Input the number of years over which the investment has grown.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 4: Calculate CAGR</h3>
                <p className="text-gray-600">
                  Click the "Calculate CAGR" button to get the result.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 5: View Result and Visualization</h3>
                <p className="text-gray-600">
                  The result will be displayed, along with a bar chart visualizing the calculation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CAGRCalculator;